import { rows } from '@/db/database'
import { currentDevice } from '@/lib/device'
import { newTempId } from '@/lib/tempId'
import { syncEngine } from '@/sync/SyncEngine'
import { PosSync } from '@/types'

type Row = PosSync.LocalRow

const key = (variationId: string, batchId: unknown) => `${variationId}|${batchId ?? ''}`

/**
 * Stock the server has not seen move yet: sales and returns made on this device that have not synced. The device
 * never edits pulled stock rows (the next pull would overwrite them); it shows the server figure plus these pending
 * moves instead, which is the rebase rule of §15.9 rule 9.
 */
export const pendingMoves = async (): Promise<Map<string, number>> => {
  const [items, returnItems, allItems, orders, counts] = await Promise.all([
    rows('order_items').where('synced').notEqual(1).toArray(),
    rows('order_return_items').where('synced').notEqual(1).toArray(),
    rows('order_items').toArray(),
    rows('orders').where('synced').notEqual(1).toArray(),
    rows('stock_adjustments').where('synced').equals(0).toArray(),
  ])
  const moves = new Map<string, number>()
  const add = (k: string, qty: number) => moves.set(k, (moves.get(k) ?? 0) + qty)
  // A cancel waiting to sync: its unsynced items never left stock, its synced ones come back (`cancelOrder`).
  const cancelled = orders.filter((o) => o.status === 'cancelled')
  const ofCancelled = (i: Row) => cancelled.some((o) => (i.order_temp_id && i.order_temp_id === o.temp_id) || (o._id && i.order_id === o._id))
  items.filter((i) => i.synced === 0 && !ofCancelled(i)).forEach((i) => add(key(String(i.product_variation_id), i.stock_purchase_transaction_id), -Number(i.quantity)))
  allItems.filter((i) => i.synced === 1 && ofCancelled(i)).forEach((i) => add(key(String(i.product_variation_id), i.stock_purchase_transaction_id), Number(i.quantity)))
  const itemById = new Map(allItems.map((i) => [i.id, i]))
  returnItems
    .filter((r) => r.synced === 0)
    .forEach((r) => {
      const sold = itemById.get(String(r.order_item_local_id))
      if (sold) add(key(String(sold.product_variation_id), sold.stock_purchase_transaction_id), Number(r.quantity))
    })
  counts.forEach((c) =>
    (c.entries as CountEntry[]).forEach((e) => add(key(e.product_variation_id, e.stock_purchase_transaction_id), e.counted - e.expected))
  )
  return moves
}

interface CountEntry {
  product_variation_id: string
  stock_purchase_transaction_id: string
  counted: number
  expected: number
}

/** POS.md §18.7 Q3: stock counts are offline only with one branch and one terminal. */
export const canAdjustOffline = async () => {
  const [branches, terminals] = await Promise.all([rows('branches').toArray(), rows('terminals').toArray()])
  return branches.filter((b) => !b.deleted_at).length === 1 && terminals.filter((t) => !t.deleted_at).length === 1
}

/** Records a count on the device; the server applies counted − expected per batch. */
export const recordStockCount = async (lines: { product_variation_id: string; stock_purchase_transaction_id: string; counted: number }[], note?: string) => {
  const device = currentDevice()
  if (!device) throw new Error('This device is not set up.')
  const [stocks, moves] = await Promise.all([rows('stocks').toArray(), pendingMoves()])
  const entries: CountEntry[] = lines.map((l) => {
    const batch = stocks.find((s) => String(s.product_variation_id) === l.product_variation_id && String(s.stock_purchase_transaction_id) === l.stock_purchase_transaction_id)
    const expected = Number(batch?.quantity ?? 0) + (moves.get(key(l.product_variation_id, l.stock_purchase_transaction_id)) ?? 0)
    return { ...l, expected }
  })
  const tempId = newTempId(device.terminal_unique_no)
  await rows('stock_adjustments').add({
    id: tempId,
    temp_id: tempId,
    synced: 0,
    branch_id: device.branch_id,
    entries,
    description: note || null,
    created_at: new Date().toISOString(),
  })
  syncEngine.requestSync()
}

/** Branch stock per variation, server figure plus pending moves. */
export const stockByVariation = async (): Promise<Map<string, number>> => {
  const [stocks, moves] = await Promise.all([rows('stocks').toArray(), pendingMoves()])
  const total = new Map<string, number>()
  const add = (variationId: string, qty: number) => total.set(variationId, (total.get(variationId) ?? 0) + qty)
  stocks.forEach((s) => add(String(s.product_variation_id), Number(s.quantity ?? 0)))
  moves.forEach((qty, k) => add(k.split('|')[0], qty))
  return total
}

export interface BatchPick {
  stock_purchase_transaction_id: string | null
  quantity: number
  expiry_date?: string | null
}

/** The soonest expiry among this branch's batches still in stock, per variation (the tile's EXP badge). */
export const nearestExpiryByVariation = async (): Promise<Map<string, string>> => {
  const [stocks, moves] = await Promise.all([rows('stocks').toArray(), pendingMoves()])
  const nearest = new Map<string, string>()
  for (const s of stocks) {
    if (!s.expiry_date) continue
    const variationId = String(s.product_variation_id)
    if (Number(s.quantity ?? 0) + (moves.get(key(variationId, s.stock_purchase_transaction_id)) ?? 0) <= 0) continue
    const expiry = String(s.expiry_date)
    const current = nearest.get(variationId)
    if (!current || new Date(expiry) < new Date(current)) nearest.set(variationId, expiry)
  }
  return nearest
}

/**
 * Splits a quantity over batches: soonest expiry first among batches with stock, then oldest first, and whatever is
 * left goes to the newest batch, where stock may go negative (oversell is allowed, §8.2, POS.md §12.6).
 */
export const pickBatches = async (variationId: string, quantity: number): Promise<BatchPick[]> => {
  const [stocks, moves] = await Promise.all([
    rows('stocks').where('product_variation_id').equals(variationId).toArray(),
    pendingMoves(),
  ])
  const batches = stocks
    .filter((s) => s.stock_purchase_transaction_id)
    .map((s: Row) => ({
      id: String(s.stock_purchase_transaction_id),
      expiry: s.expiry_date ? new Date(String(s.expiry_date)).getTime() : null,
      created: new Date(String(s.created_at ?? 0)).getTime(),
      available: Number(s.quantity ?? 0) + (moves.get(key(variationId, s.stock_purchase_transaction_id)) ?? 0),
      expiry_date: s.expiry_date ? String(s.expiry_date) : null,
    }))
  if (batches.length === 0) return [{ stock_purchase_transaction_id: null, quantity }]

  const order = [
    ...batches.filter((b) => b.expiry !== null).sort((a, b) => a.expiry! - b.expiry! || a.created - b.created),
    ...batches.filter((b) => b.expiry === null).sort((a, b) => a.created - b.created),
  ]
  const picks: BatchPick[] = []
  let left = quantity
  for (const batch of order) {
    if (left <= 0) break
    const take = Math.min(left, Math.max(batch.available, 0))
    if (take > 0) {
      picks.push({ stock_purchase_transaction_id: batch.id, quantity: take, expiry_date: batch.expiry_date })
      left -= take
    }
  }
  if (left > 0) {
    const newest = [...batches].sort((a, b) => b.created - a.created)[0]
    const existing = picks.find((p) => p.stock_purchase_transaction_id === newest.id)
    if (existing) existing.quantity += left
    else picks.push({ stock_purchase_transaction_id: newest.id, quantity: left, expiry_date: newest.expiry_date })
  }
  return picks
}
